import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShareNetwork, TwitterLogo, FacebookLogo, WhatsappLogo, LinkSimple, Newspaper } from "@phosphor-icons/react";
import api from "../lib/api";

function ShareMenu({ title, body }) {
  const [open, setOpen] = useState(false);
  const url = encodeURIComponent(window.location.origin + "/news");
  const text = encodeURIComponent(`${title} — ${body}`);
  const links = [
    { id: "tw", icon: TwitterLogo, label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${text}&url=${url}` },
    { id: "fb", icon: FacebookLogo, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}` },
    { id: "wa", icon: WhatsappLogo, label: "WhatsApp", href: `https://wa.me/?text=${text}%20${url}` },
  ];
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(decodeURIComponent(text) + " — " + decodeURIComponent(url));
    setOpen(false);
  };
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }} className="text-ink-500 hover:text-cobalt-500 p-1" data-testid="news-share-trigger">
        <ShareNetwork size={16} weight="bold" />
      </button>
      {open && (
        <div className="absolute right-0 top-7 bg-white border border-ink-200 rounded-2xl shadow-soft p-1.5 z-30 min-w-[160px]">
          {links.map((l) => {
            const I = l.icon;
            return (
              <a key={l.id} href={l.href} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm rounded-xl hover:bg-cobalt-50 text-ink-700">
                <I size={16} weight="bold" /> {l.label}
              </a>
            );
          })}
          <button onClick={copy} className="flex items-center gap-2 px-3 py-2 text-sm rounded-xl hover:bg-cobalt-50 text-ink-700 w-full">
            <LinkSimple size={16} weight="bold" /> Copy link
          </button>
        </div>
      )}
    </div>
  );
}

export default function NewsFeed({ compact = false }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get("/news").then(({ data }) => {
      setItems(data.items || []);
      setUpdatedAt(data.updated_at);
    });
  }, []);

  return (
    <section className="py-20" data-testid="news-feed">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <Newspaper size={14} weight="bold" /> {t("news.eyebrow")}
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{t("news.title")}</h2>
            <p className="mt-3 text-ink-700">{t("news.sub")}</p>
          </div>
          {updatedAt && (
            <div className="text-xs text-ink-500">
              {t("news.updated")} · {new Date(updatedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.slice(0, compact ? 3 : 6).map((n) => (
            <article key={n.id} className="card-soft p-6 flex flex-col" data-testid={`news-${n.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="text-3xl">{n.emoji}</div>
                <ShareMenu title={n.title} body={n.body} />
              </div>
              <div className="font-display font-semibold text-lg leading-tight tracking-tight mb-2">{n.title}</div>
              <p className="text-sm text-ink-700 leading-relaxed flex-1">{n.body}</p>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="pill bg-cobalt-50 text-cobalt-600">{n.tag}</span>
                <span className="text-ink-500">{n.region}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
